import React, { useCallback, useEffect, useContext, useState } from "react";
import { Button, Text, LegacyCard as Card, FormLayout, Modal, InlineStack, BlockStack } from "@shopify/polaris";
import { ChevronLeftIcon, ChevronRightIcon } from "@shopify/polaris-icons";
import { useMutation, useQuery } from "@apollo/client";

// import constant
import constant from "lib/constant/constant";

// import components
import { DropZone, SkeletonCard, Toast } from "lib/components";

// import mutations
import { ADD_PRODUCT_IMAGE, DELETE_PRODUCT_IMAGE, UPLOAD_IMAGE } from "app/product/apollo/mutations";

// import helpers
import { baseHelper, errorHelper, imageHelper } from "lib/helpers";

//  import queries
import { GET_PRODUCT } from "app/product/apollo/queries";

// import context
import { PrivateContext } from "lib/context";
import { ProductContext } from "app/product/modules/generic/context";
import { Span } from "app/product/modules/generic/style/style";
import { useParams } from "react-router-dom";

const EditImage = () => {
  const { handleTabChange, isVariant, setBanner, setDescription, setTitle } = useContext(ProductContext);
  const { cms } = useContext(PrivateContext);
  const { id } = useParams();
  const [message, setMessage] = useState("");
  const [value, setValue] = useState({});
  const [loadingId, setLoadingId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isRearrange, setIsRearrange] = useState(false);
  const [imageStartUploading, setImageStartUploading] = useState(false);
  const [imageToBeUploaded, setImageToBeUploaded] = useState([]);
  const [uploadImage, { loading: imageLoading }] = useMutation(UPLOAD_IMAGE);
  const [existingProductImages, setExistingProductImages] = useState([]);
  const [rearrange, setRearrange] = useState([...existingProductImages]);
  const [addProductImage, { loading: addImageLoading }] = useMutation(ADD_PRODUCT_IMAGE);
  const [deleteImage, { loading: deleteImageLoading }] = useMutation(DELETE_PRODUCT_IMAGE);
  const { CRITICAL, gql } = constant;
  const [disabledButton, setDisabledButton] = useState(true);
  setDescription(cms("label.editImage"));
  setTitle(cms("label.title.image"));
  const handleChange = useCallback(
    (fieldName, fieldValue) => {
      setValue({
        ...value,
        [fieldName]: fieldValue,
      });
    },
    [value]
  );

  const { loading: productLoading, data: productData } = useQuery(GET_PRODUCT, {
    variables: { input: { id } },
  });

  useEffect(() => {
    if (productData) {
      const productResponse = baseHelper.getResponseData(productData, gql.GET_PRODUCT) || {};
      const { _id, title, images, updatedAt, status } = productResponse;
      const product = {
        _id,
        title,
        images,
        updatedAt,
        status,
      };
      setValue(product);
      setExistingProductImages((productResponse && productResponse.images) || []);
    }
  }, [gql.GET_PRODUCT, productData]);

  const productListError = baseHelper.getResponseError(productData, gql.GET_PRODUCT);

  useEffect(() => {
    setRearrange([...existingProductImages]);
  }, [existingProductImages]);

  useEffect(() => {
    if (productListError) {
      setBanner({ isOpen: true, status: CRITICAL, title: productListError });
    }
  }, [CRITICAL, cms, productListError, setBanner]);

  useEffect(() => {
    if (value && value.images && value.images.length) {
      setDisabledButton(false);
    } else {
      setDisabledButton(true);
    }
  }, [value]);

  if (productLoading) {
    return <SkeletonCard />;
  }

  // const learnMore = (productItem, title) => {
  //   setSheetActive(true);
  //   setSheetTitle(title);
  //   setSheetContent(cms("label.todo")`${productItem}`);
  // };

  const handleAddImage = (data) => {
    const productImages = (value && value.images) || [];
    const uploadedImages = [...productImages, ...data];
    setImageToBeUploaded([...imageToBeUploaded, ...data]);
    handleChange(constant.IMAGES, uploadedImages);
  };

  const removeImage = (index, buttonIndex) => {
    const productImages = value.images || [];
    if (index < 0 || productImages.length <= 0) {
      return;
    }
    const exitingProductImageLength = existingProductImages && existingProductImages.length;
    imageToBeUploaded.splice(buttonIndex - exitingProductImageLength, 1);
    setImageToBeUploaded(imageToBeUploaded);
    productImages.splice(buttonIndex, 1);
    handleChange(constant.IMAGES, productImages);
  };

  const addImageData = async (formValues, productId) => {
    try {
      const val = await addProductImage({
        variables: {
          input: formValues,
        },
      });

      const resData = baseHelper.getResponseData(val.data, gql.ADD_PRODUCT_IMAGE);
      const resError = baseHelper.getResponseError(val.data, gql.ADD_PRODUCT_IMAGE);
      if (resError) {
        const banner = {
          action: null,
          isOpen: true,
          status: CRITICAL,
          title: resError,
        };
        setBanner(banner);
        return;
      }
      setIsOpen(false);
      setIsRearrange(false);
      if (resData && productId) {
        setMessage(cms("message.success.image"));
      }
      if (resData && !productId) {
        setMessage(cms("message.success.image"));
        setTimeout(() => {
          if (isVariant) {
            handleTabChange(2);
            return;
          }
          handleTabChange(3);
        }, 1500);
      }
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const imageUpload = (formField) => {
    const formValues = formField || {};
    const { images = [] } = formValues || {};
    let hasImageError = false;
    if (images && images.length) {
      const uploadedImages = [];
      const uploadImageCallback = (imageResponse) => {
        const { imageUrl = null, imageId = null } = imageResponse;
        if (imageUrl) {
          const uploadedImageData = {
            imageUrl,
            imageId,
          };

          uploadedImages.push(uploadedImageData);
        }
        if (hasImageError) {
          return;
        }
        if (uploadedImages && uploadedImages.length > 0 && uploadedImages.length === images.length) {
          setImageStartUploading(false);
          formValues.images = uploadedImages;
          addImageData(formValues);
        }
      };
      formValues.images.map(async (img) => {
        try {
          if (hasImageError) {
            return;
          }
          let response = {};
          if (typeof img === "string") {
            response = await uploadImage({ variables: { input: { image: img, productId: id } } });
          } else {
            uploadImageCallback(img);
            return;
          }

          if (!imageStartUploading) {
            setImageStartUploading(true);
          }
          const imageResponse = baseHelper.getResponseData(response.data, gql.UPLOAD_IMAGE);
          const imageError = baseHelper.getResponseError(response.data, gql.UPLOAD_IMAGE);
          if (imageError) {
            setImageStartUploading(false);
            hasImageError = true;
            setBanner((prev) => ({
              ...prev,
              action: null,
              isOpen: true,
              status: CRITICAL,
              title: imageError,
            }));
          }
          uploadImageCallback(imageResponse);
        } catch (uploadImageError) {
          setImageStartUploading(false);
          hasImageError = true;
          setBanner((prev) => ({
            ...prev,
            action: null,
            isOpen: true,
            status: CRITICAL,
            title: cms("label.notSave"),
          }));
        }
      });
      setImageStartUploading(false);
    } else {
      addImageData(formValues);
    }
    return null;
  };

  const handleDeleteImage = (image) => {
    setMessage("");
    const { _id = "" } = value;
    const { position = 0, url = "" } = image;

    if (!position || url === "") {
      return;
    }

    const formData = {
      id: position || "",
      productId: _id,
      url,
    };
    setLoadingId(position);
    deleteImage({ variables: { input: formData } })
      .then((res) => {
        setLoadingId("");
        const responseError = baseHelper.getResponseError(res.data, gql.DELETE_PRODUCT_IMAGE);
        if (responseError) {
          setBanner({ isOpen: true, status: CRITICAL, title: responseError });
        }
        const responseData = baseHelper.getResponseData(res.data, gql.DELETE_PRODUCT_IMAGE);
        if (responseData) {
          setMessage(cms("message.success.imageDelete"));
          // Build a new image list without mutating existing objects
          // 1) Remove the image matching the deleted position
          // 2) Reindex positions greater than the deleted one
          const productImages = existingProductImages
            .filter((img) => img.position !== position)
            .map((img) => (img.position > position ? { ...img, position: img.position - 1 } : img));

          setExistingProductImages(productImages);
          setValue({ ...value, images: [...productImages, ...imageToBeUploaded] });
        }
      })
      .catch((exception) => {
        setLoadingId("");
        setBanner({ isOpen: true, status: CRITICAL, title: errorHelper.parse(exception) });
      });
  };

  const onRearrangeSubmit = () => {
    const { _id: productId = "" } = value;
    const images = rearrange.map(({ imageId, imageUrl }) => ({ imageId, imageUrl }));
    setValue({ _id: productId, images });
    addImageData({ productId, images }, productId);
    setExistingProductImages([...rearrange]);
  };

  const onFormSubmit = async () => {
    const { images: formImage = [], _id = "" } = value;
    try {
      const formValues = {
        productId: _id,
        images: formImage,
      };
      await imageUpload(formValues);
    } catch (exception) {
      setBanner((prev) => ({
        ...prev,
        isOpen: true,
        status: CRITICAL,
        title: errorHelper.parse(exception),
      }));
    }
  };

  const renderModal = () => {
    const images = rearrange;
    const moveImage = (fromIndex, toIndex) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= images.length ||
        toIndex >= images.length ||
        fromIndex === toIndex
      ) {
        return;
      }
      setIsRearrange(true);
      const nextImages = [...images];
      const [moved] = nextImages.splice(fromIndex, 1);
      nextImages.splice(toIndex, 0, moved);
      setRearrange(nextImages);
    };
    return (
      <BlockStack>
        <InlineStack gap="400">
          {images.map(({ imageUrl, imageId }, index) => (
            <BlockStack key={imageId || imageUrl || index} gap="200">
              <Span className="Polaris-Thumbnail Polaris-Thumbnail--sizeLarge">
                <img
                  src={imageHelper.resize({ url: imageUrl, type: constant.imageTypes.THUMBNAIL })}
                  alt={`${index}-img`}
                />
              </Span>
              <InlineStack align="center" gap="200">
                <Button
                  size="slim"
                  disabled={index === 0}
                  onClick={() => moveImage(index, index - 1)}
                  accessibilityLabel={cms("button.previous")}
                  icon={ChevronLeftIcon}
                />
                <Button
                  size="slim"
                  disabled={index === images.length - 1}
                  onClick={() => moveImage(index, index + 1)}
                  accessibilityLabel={cms("button.next")}
                  icon={ChevronRightIcon}
                />
              </InlineStack>
            </BlockStack>
          ))}
        </InlineStack>
      </BlockStack>
    );
  };
  return (
    <>
      <Card
        title={[
          cms("label.productImage"),
          value && value.title && (
            <Text as="span" tone="subdued">
              {value.updatedAt && (
                <Text variant="bodySm" as="p">{`${baseHelper.lastUpdateDate(value.updatedAt)}`}</Text>
              )}
            </Text>
          ),
        ]}
        id="productImage"
        actions={[
          {
            content: existingProductImages && existingProductImages.length > 1 && cms("modal.rearrange"),
            onAction: () => existingProductImages && existingProductImages.length > 1 && setIsOpen(true),
            disabled: !(existingProductImages && existingProductImages.length > 1),
          },
        ]}
        // actions={[
        //   {
        //     content: cms("label.more"),
        //     onAction: () => {
        //       learnMore("productImage", cms("label.productImage"));
        //     },
        //   },
        // ]}
      >
        <Card.Section>
          <p>{cms("label.caption.image")}</p>

          <Modal
            open={isOpen}
            onClose={() => {
              setIsOpen(false);
              setIsRearrange(false);
              setRearrange([...existingProductImages]);
            }}
            title={cms("modal.rearrange")}
            secondaryActions={[{
              content: cms("button.cancel"),
              disabled: isRearrange && addImageLoading,
              onAction: () => {
                setIsOpen(false);
                setIsRearrange(false);
                setRearrange([...existingProductImages]);
              },
            }]}
            primaryAction={{
              content: cms("modal.button"),
              disabled: !isRearrange,
              loading: isRearrange && addImageLoading,
              onAction: onRearrangeSubmit,
            }}
          >
            <Modal.Section>{renderModal()}</Modal.Section>
          </Modal>
          <FormLayout>
            <BlockStack gap="4">{cms("section.form.section.price.description")}</BlockStack>
            <DropZone
              id="addProductDropZone"
              onAdd={handleAddImage}
              onRemove={removeImage}
              size={20}
              allowMultiple
              existingImageList={existingProductImages || []}
              removeExistingImage={handleDeleteImage}
              loadingPosition={loadingId}
              disabled={addImageLoading || deleteImageLoading || imageLoading}
              setDisabledButton={setDisabledButton}
            />
          </FormLayout>
          <div className="toast">
            <Toast message={message} setToast={setMessage} />
          </div>
        </Card.Section>
      </Card>
      <br />
      <InlineStack align="space-between">
        <Button
          onClick={() => {
            const tab = isVariant ? 0 : 1;
            handleTabChange(tab);
          }}
        >
          {cms("button.previous")}
        </Button>
        <InlineStack gap="400">
          {value && value.status && value.status.toLowerCase() === constant.NEW && (
            <Button onClick={() => (isVariant ? handleTabChange(2) : handleTabChange(3))}>{cms("button.skip")}</Button>
          )}
          <Button
            variant="primary"
            onClick={() => onFormSubmit()}
            disabled={deleteImageLoading || disabledButton}
            loading={!isRearrange && (addImageLoading || imageLoading)}
          >
            {cms("button.save")}
          </Button>
        </InlineStack>
      </InlineStack>
    </>
  );
};

export default EditImage;
